<template>
  <div>
    <modal name="modal-trip-tracker" class="asset-tracker modal-inner" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="500" height="auto" :scrollable="true" :resizable="true" :adaptive="true" :reset="true" @before-open="loadTemplates" @before-close="reset">
      <span class="close-button" @click="hide()"><BaseIcon icon="times-circle" class="text-white"/></span>
      <div class="modal-header less-margin-bottom bg-primary-50 font-serif">
        <div class="flex items-center pl-4">
          <BaseIcon icon="route" class="text-2xl mr-3 text-white" />
          <div>
            <h2 class="text-lg font-bold text-white">{{ $t('trip_planner.title') }}</h2>
            <p class="text-sm text-white opacity-90">{{ $t('trip_planner.subtitle') }}</p>
          </div>
        </div>
      </div>
      <div class="modal-body less-side-padding" v-bind:class="{ 'less-bottom-padding': !renderDirection }">
        <ValidationObserver ref="formAssetTracker">
          <div class="flex flex-wrap">
            <div class="w-full lg:w-9/12 relative">

              <!-- ── Map legend (visible only during route comparison) ──────── -->
              <transition name="fade">
                <div v-if="optimizedPath.length > 0" class="absolute bottom-6 left-4 z-10 bg-white rounded-xl shadow-lg border border-gray-200 px-3 py-2.5 text-xs pointer-events-none" style="min-width:170px">
                  <div class="font-semibold text-gray-600 mb-1.5 text-xxs uppercase tracking-wide">{{ $t('trip_planner.legend_title') }}</div>
                  <div class="flex items-center gap-2 mb-1">
                    <svg width="28" height="6"><line x1="0" y1="3" x2="28" y2="3" stroke="#1A8DE9" stroke-width="4" stroke-linecap="round" /></svg>
                    <span class="text-gray-700">{{ $t('trip_planner.legend_original') }}</span>
                    <span class="ml-auto font-mono text-gray-500">{{ originalDistance ? (originalDistance/1000).toFixed(1)+' km' : '' }}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <svg width="28" height="6"><line x1="0" y1="3" x2="28" y2="3" stroke="#F29100" stroke-width="5" stroke-linecap="round" /></svg>
                    <span class="text-securdia-orange font-semibold">{{ $t('trip_planner.legend_optimized') }}</span>
                    <span class="ml-auto font-mono text-securdia-orange">{{ optimizationResult ? (optimizationResult.optimizedDistance/1000).toFixed(1)+' km' : '…' }}</span>
                  </div>
                  <div v-if="optimizationResult && optimizationResult.distanceSaved > 0" class="mt-1.5 pt-1.5 border-t border-gray-100 text-securdia-orange font-semibold text-center">
                    {{ $t('trip_planner.legend_saves') }} {{ (optimizationResult.distanceSaved/1000).toFixed(1) }} km · {{ Math.round(optimizationResult.timeSaved/60) }} min
                  </div>
                </div>
              </transition>
              <!-- ── /Map legend ───────────────────────────────────────────── -->

              <GmapMap :center="center" :zoom="zoom" map-type-id="roadmap" class="w-full h-90vh" ref="map" @click="closeTrackedPointInfo">
                <GmapInfoWindow :options="infoOptions" :position="infoWindowPos" :opened="infoWinOpen" @closeclick="infoWinOpen = false"></GmapInfoWindow>
                <GmapMarker :position="center" :clickable="true" :draggable="false" :icon="{ url: infoOptions.icon }"></GmapMarker>
                <GmapMarker :position="startLocation" @dragend="changeLocationStart" :options="{ draggable: true, clickable: true, label: 'A', visible: startMarkerVisible }" />
                <GmapMarker :position="endLocation" @dragend="changeLocationEnd" :options="{ draggable: true, clickable: true, label: 'B', visible: endMarkerVisible }" />
          
                <DirectionsRenderer v-if="renderDirection" travelMode="DRIVING" stopover="true" :origin="startLocation" :destination="endLocation" :waypoints="waypoints" :optimizeWaypoints="false" :options="{ draggable: optimizedPath.length === 0, polylineOptions: { strokeColor: '#1A8DE9', strokeOpacity: optimizedPath.length > 0 ? 0.35 : 0.9, strokeWeight: 6, zIndex: 100 } }" @directions_changed="directionChanged" @error_direction="handleDirectionError" />
                <GmapPolyline :path="definedRoute" :options="{ strokeColor: '#1A8DE9', strokeOpacity: 0.9, strokeWeight: 6, zIndex: 100 }" />
                <!-- Optimized preview — independent GmapPolyline, hidden when switcher is on "Original" -->
                <GmapPolyline v-if="renderDirection && optimizedPath.length > 0 && showOptimizedPreview" :path="optimizedPath" :options="{ strokeColor: '#F29100', strokeOpacity: 0.95, strokeWeight: 8, zIndex: 101 }" />
                <!-- show when trip is completed -->
                
                <DirectionsRenderer v-if="!renderDirection" travelMode="DRIVING" stopover="true" :origin="startLocation" :destination="endLocation" :waypoints="waypoints" :options="{ draggable: true, polylineOptions: { strokeColor: '#1A8DE9', strokeOpacity: 0.9, strokeWeight: 6, zIndex: 100 } }" />
                <GmapPolyline v-if="!renderDirection && updateInfo && updateInfo.defined_route" :path="updateInfo.defined_route" :options="{ strokeColor: '#1A8DE9', strokeOpacity: 0.9, strokeWeight: 6, zIndex: 100 }" />
                <GmapPolyline 
                  v-for="(point, index) in (updateInfo && updateInfo.tracked_route) ? updateInfo.tracked_route : []" 
                    :key="'segment-polyline-' + index" 
                    :path="updateInfo.tracked_route.slice(index, index + 2)"
                    :options="{ 
                      strokeColor: '#DC2626',
                      strokeOpacity: 0.95,
                      strokeWeight: 6,
                      zIndex: 101,
                    }"
                    @mouseover="showTrackedPointInfo(point, index)"
                    @mouseout="closeTrackedPointInfoUnlessPinned"
                    @click="pinTrackedPointInfo(point, index)"
                  ></GmapPolyline>
                <GmapCircle v-for="(pin, index) in waitingCircles" :key="index" :center="pin" :options="{ fillColor: '#F59E0B', fillOpacity: 0.4, radius: 200, strokeColor: '#F59E0B', strokeOpacity: 0.8, strokeWeight: 2, zIndex: 50 }" />
              </GmapMap>
            </div>
            <div class="w-full lg:w-3/12 flex flex-col pl-4">
              <!-- WIZARD START -->
              <div v-if="renderDirection" class="wizard-container flex flex-col h-full relative" style="height: 100%; min-height: 500px;">

                  <!-- WIZARD HEADER -->
                  <div class="wizard-header mb-4 mt-2">
                      <div class="flex justify-between items-center mb-2">
                          <h3 class="font-bold text-gray-700">{{ stepTitle }}</h3>
                          <div class="flex items-center gap-2">
                            <span class="text-xs text-gray-500">{{ currentStep }} / {{ totalSteps }}</span>
                            <button class="btn-secondary-outline text-xs px-2 py-1" @click.prevent="toggleTemplateList">
                              <BaseIcon icon="folder" class="mr-1" />{{ $t('trip_planner.templates') }}
                            </button>
                          </div>
                      </div>
                      <div class="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700">
                          <div class="bg-blue-600 h-1.5 rounded-full transition-all duration-300 ease-in-out" :style="{ width: (currentStep / totalSteps) * 100 + '%' }"></div>
                      </div>

                      <!-- Template selector -->
                      <transition name="fade" mode="out-in">
                        <div v-if="showTemplateList" class="mt-3 border bg-gray-50 rounded shadow-sm" key="templates">
                          <div class="flex items-center justify-between border-b p-2">
                            <div class="font-semibold text-sm">{{ $t('trip_planner.route_templates') }}</div>
                            <button class="text-xs text-gray-500 hover:text-gray-700" @click.prevent="toggleTemplateList">
                              <BaseIcon icon="times" />
                            </button>
                          </div>

                          <div v-if="templates.length === 0" class="text-sm text-gray-500 p-2 text-center">{{ $t('trip_planner.no_templates') }}</div>
                          <div v-else class="custom-scrollbar max-h-40 overflow-y-auto">
                            <div v-for="(t, i) in templates" :key="t.id" class="mb-2 p-2 hover:bg-gray-50" :class="[i !== templates.length - 1 ? 'border-b' : '' ]">
                              <div class="flex items-start justify-between">
                                <div class="flex-grow">
                                  <div class="font-medium text-sm">{{ t.name }}</div>
                                  <div class="text-xs text-gray-500">{{ t.destinations.length }} {{ $t('trip_planner.stops') }} • {{ $t('trip_planner.buffer') }}: {{ t.bufferZone }}m</div>
                                  <div class="text-xs text-gray-400">{{ formatDate(new Date(t.created)) }}</div>
                                </div>
                                <div class="flex items-center gap-1 ml-2">
                                  <button class="btn-blue text-xs px-2 py-1" @click.prevent="applyTemplate(i)" title="Apply template">
                                    <BaseIcon icon="check" class="mr-1" />
                                    {{ $t('trip_planner.use_template') }}
                                  </button>
                                  <button class="btn-red-outline text-xs px-2 py-1" @click.prevent="deleteTemplate(i)" title="Delete template">
                                    <BaseIcon icon="trash" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div> 
                        </div>
                      </transition>
                  </div>

                  <!-- WIZARD BODY -->
                  <div class="wizard-body border-t-2 border-indigo-500 border-dashed flex-grow mb-16 custom-scrollbar pr-1 overflow-y-auto max-h-[calc(100vh - 200px)]" style="max-height: calc(100vh - 185px);">
                      
                      <!-- STEP 1: Select Alarm -->
                      <transition name="fade" mode="out-in">
                          <div v-if="currentStep === 1" key="step1">
                             <div class="mt-4">
                                <label class="text-sm font-semibold text-gray-600 block mb-2">{{ $t('trip_planner.route_name_label') }}</label>
                                <input v-model="routeName" type="text" :placeholder="$t('trip_planner.route_name_placeholder')" class="border rounded p-2 w-full" />
                                <p class="text-xs text-gray-500 mt-1">{{ $t('trip_planner.route_name_hint') }}</p>
                              </div>

                              <div class="mt-4">
                                  <label class="text-sm font-semibold text-gray-600 block mb-2">{{ $t('trip_planner.select_alarm') }}</label>
                                  <treeselect id="unit-in-modal" :disabled="isAlarmSelectDisabled" v-model="selectedAlarm" :options="$store.getters.getUnitOptions" @input="deviceSelected(selectedAlarm)" :multiple="false" :disable-branch-nodes="true" value-consists-of="LEAF_PRIORITY" :noChildrenText="$t('trip_planner.no_activity')" />
                              </div>
                              <!-- show in only edit mode -->
                              <div v-if="isEditMode && destinations[0].address && destinations[destinations.length -1].address">
                                <div class="w-full mt-5 ">
                                  <div class="text-sm text-center">
                                    {{ destinations[0].address }}
                                  </div>
                                  <div class="text-center my-1">
                                    <BaseIcon icon="arrow-down" class="text-sm" />
                                  </div>
                                  <div class="text-sm text-center">
                                    {{ destinations[destinations?.length - 1].address }}
                                  </div>

                                  <div class="border-b border-secondary-400 my-5"></div>

                                  <div class="flex justify-between">
                                    <div class="text-sm">
                                      {{ $t('trip_planner.departure_time') }}
                                    </div>
                                    <div class="text-sm">
                                      {{ formatDate(departureTime) }}
                                    </div>
                                  </div>
                                  <div class="flex justify-between mt-1">
                                    <div class="text-sm">
                                      {{ $t('trip_planner.estimated_arrival') }}
                                    </div>
                                    <div class="text-sm">
                                      {{ expectedArrival }}
                                    </div>
                                  </div>
                                  <div class="flex justify-between mt-1">
                                    <div class="text-sm">
                                      {{ $t('trip_planner.driving_time') }}
                                    </div>
                                    <div class="text-sm">
                                      {{ formatDuration(totalDuration) }}
                                    </div>
                                  </div>
                                  <div class="flex justify-between mt-1">
                                    <div class="text-sm">
                                      {{ $t('trip_planner.break_time') }}
                                    </div>
                                    <div class="text-sm">
                                      {{ totalBreakTime }} min
                                    </div>
                                  </div>
                                  <div class="flex justify-between mt-1">
                                    <div class="text-sm">
                                      {{ $t('trip_planner.max_stop_time') }}
                                    </div>
                                    <div class="text-sm">
                                      {{ maxWaitingTime }}
                                    </div>
                                  </div>
                                  <div class="flex justify-between mt-1">
                                    <div class="text-sm">
                                      {{ $t('trip_planner.allowed_stops') }}
                                    </div>
                                    <div class="text-sm">
                                      {{ maxStops }}
                                    </div>
                                  </div>
                                  <div class="clear-both mt-1 flex justify-between ">
                                    <div class="text-sm">
                                      {{ $t('trip_planner.email') }}
                                    </div>
                                    <div class="text-sm">
                                      {{ updateInfo ? updateInfo.email_address : '-' }}
                                    </div>
                                  </div>
                                </div>
                              </div>
                          </div>
                      </transition>

                      <!-- STEP 2: Route Planning -->
                      <transition name="fade" mode="out-in">
                          <div v-if="currentStep === 2" key="step2">
                              <div class="mt-2 text-xs text-secondary-500 mb-2 italic">{{ $t('trip_planner.drag_drop_hint') }}</div>
                              <draggable :key="listRenderKey" class="list-group" tag="ul" v-model="destinations" v-bind="dragOptions" :draggable="'.draggable'" @end="onDragEnd">
                                  <transition-group type="transition">
                                      <li class="mt-2 destination list-none list-group-item" v-for="(des, i) in destinations" :key="'dest-'+i" :class="{ draggable: des.address !== '' || des.type === 'break' }">
                                        <div class="flex">
                                          <div class="pl-0 py-2" style="width: 20px;" v-if="i > 0">
                                              <BaseIcon v-if="des.type != 'break'"  icon="arrow-down" class="stop-icon" />
                                              <BaseIcon v-if="des.type != 'break'" icon="arrows-alt" class="drag-icon" />
                                          </div>
                                          <div class="flex-grow">
                                            <!-- Break Component -->
                                            <div v-if="des.type === 'break'" class="break-stop bg-yellow-50 p-2 rounded border border-yellow-200">
                                              <div class="flex items-center gap-2 mb-2">
                                                <BaseIcon icon="coffee" class="text-yellow-600" />
                                                <span class="font-semibold text-xs text-yellow-800">{{ $t('trip_planner.break_stop') }}</span>
                                              </div>
                                              <div class="flex gap-2">
                                                <select v-model.number="des.breakDuration" class="input-field text-xs py-1 px-2 w-1/2 rounded border border-yellow-300 bg-yellow-50">
                                                  <option :value="5">5 {{ $t('trip_planner.minutes') }}</option>
                                                  <option :value="10">10 {{ $t('trip_planner.minutes') }}</option>
                                                  <option :value="15">15 {{ $t('trip_planner.minutes') }}</option>
                                                  <option :value="30">30 {{ $t('trip_planner.minutes') }}</option>
                                                  <option :value="45">45 {{ $t('trip_planner.minutes') }}</option>
                                                  <option :value="60">1 {{ $t('trip_planner.hour') }}</option>
                                                  <option :value="90">1.5 {{ $t('trip_planner.hours') }}</option>
                                                  <option :value="120">2 {{ $t('trip_planner.hours') }}</option>
                                                </select>
                                                <select v-model="des.breakType" class="input-field text-xs py-1 px-2 w-1/2 rounded border border-yellow-300 bg-yellow-50">
                                                  <option v-for="bt in breakTypes" :key="bt.value" :value="bt.value">
                                                    {{ $t(bt.label) }}
                                                  </option>
                                                </select>
                                              </div>
                                              <div class="mt-2">
                                                <input v-model="des.note" type="text" :placeholder="$t('trip_planner.break_note_placeholder')" class="input-field text-xs py-1 px-2 w-full rounded border border-yellow-300 bg-yellow-50" />
                                              </div>
                                            </div>

                                            <!-- Destination Input -->
                                            <div v-else>
                                              <GmapAutocomplete :ref="'autocomplete-' + i" class="w-full" :key="i" :placeholder="i === 0 ? $t('trip_planner.start_location') : `${$t('trip_planner.destination')} ${destinations.slice(0, i).filter(d => d.type === 'destination').length}`" @place_changed="setStopsPlace($event, i)" :options="{ fields: ['geometry', 'formatted_address', 'address_components', 'name'] }" />
                                              <!-- Add break after button: only when allowed (not first/last and no existing break in gap) -->
                                              <div class="mt-1 flex justify-end">
                                                <button v-if="canAddBreakAt(i)" @click="addBreak(i)" class="text-xs text-yellow-600 hover:text-yellow-800 inline-flex items-center gap-1">
                                                  <BaseIcon icon="plus-circle" class="text-xs" />
                                                  <BaseIcon icon="coffee" class="text-xs" />
                                                  <span>{{ $t('trip_planner.add_break') || 'Add break' }}</span>
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                          <div class="px-1 py-2 cursor-pointer" style="width: 20px;" v-if="i > 0">
                                            <div @click="removeDestination(i)">
                                              <BaseIcon icon="times-circle" class="close-icon mt-1" />
                                            </div>
                                          </div>
                                        </div>
                                      </li>
                                  </transition-group>
                              </draggable>

                              <div class="text-right text-sm mb-5 mt-2"> 
                                <div class="flex gap-2 justify-end mt-3">
                                  <button @click="addDestination()" class="cursor-pointer btn-secondary-outline text-xs px-2 py-1">
                                    <BaseIcon icon="map-marker-alt" class="mr-1" />
                                    {{ $t('trip_planner.add_destination') }}
                                  </button>
                                </div>
                              </div>

                              <!-- Summary Box (Distance/Time) -->
                              <div class="trip-summary mb-3 bg-gray-50 p-3 rounded text-sm border border-gray-200" v-if="totalDuration">
                                  <div class="flex justify-between mb-1">
                                    <span class="text-gray-600">{{ $t('trip_planner.distance') }}</span>
                                    <span class="font-bold">{{ (totalDistance / 1000).toFixed(1) }} km</span>
                                  </div>
                                  <div class="flex justify-between mb-1">
                                    <span class="text-gray-600">{{ $t('trip_planner.driving_time') }}</span>
                                    <span class="font-bold">{{ formatDuration(totalDuration) }}</span>
                                  </div>
                                  <div class="flex justify-between mb-1">
                                    <span class="text-gray-600">{{ $t('trip_planner.break_time') }}</span>
                                    <span class="font-bold">{{ totalBreakTime }} min</span>
                                  </div>
                                  <div class="flex justify-between font-bold border-t border-gray-200 pt-2 mt-2">
                                    <span>{{ $t('trip_planner.total_time') }}</span>
                                    <span>{{ formatDuration(totalDuration + totalBreakTime * 60) }}</span>
                                  </div>
                              </div>

                              <!-- ── Optimize Stop Order ─────────────────────────── -->
                              <div v-if="totalDuration && hasOptimizableStops" class="mb-3">

                                <!-- Trigger button (idle state) -->
                                <button
                                  v-if="!optimizeRoute && !optimizationResult"
                                  @click="fetchOptimizedRoute()"
                                  class="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg border border-dashed border-securdia-orange text-securdia-orange bg-primary-50 hover:bg-primary-100 text-xs font-semibold transition-all">
                                  {{ $t('trip_planner.optimize_stop_order') }}
                                </button>

                                <!-- Calculating (optimizeRoute=true, no result yet) -->
                                <div
                                  v-else-if="optimizeRoute && !optimizationResult"
                                  class="flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary-50 border border-primary-200 text-xs text-primary-600">
                                  <LoadingSpinner :isLoading="true" />
                                  {{ $t('trip_planner.optimizing') }}
                                  <button @click="discardOptimization" class="ml-2 text-xxs text-gray-400 hover:text-gray-600 underline">{{ $t('user_actions.cancel') }}</button>
                                </div>

                                <!-- Results panel -->
                                <div v-else-if="optimizationResult" class="rounded-lg overflow-hidden border" :class="optimizationResult.distanceSaved > 0 ? 'border-securdia-orange' : 'border-gray-300'">
                                  <!-- Header bar: orange when savings found, gray when already optimal -->
                                  <div class="px-3 py-2 flex items-center justify-between" :class="optimizationResult.distanceSaved > 0 ? 'bg-securdia-orange' : 'bg-gray-400'">
                                    <div class="flex items-center gap-1.5 text-white">
                                      <BaseIcon :icon="optimizationResult.distanceSaved > 0 ? 'check-circle' : 'info-circle'" class="text-sm" />
                                      <span class="text-xs font-bold">{{ optimizationResult.distanceSaved > 0 ? $t('trip_planner.optimization_found') : $t('trip_planner.route_already_optimal') }}</span>
                                    </div>
                                    <span v-if="optimizationResult.distanceSaved > 0" class="text-xs font-bold text-white opacity-90">{{ optimizationResult.efficiencyGain.toFixed(1) }}% {{ $t('trip_planner.more_efficient') }}</span>
                                  </div>

                                  <!-- View switcher: toggle input -->
                                  <div v-if="optimizationResult.distanceSaved > 0" class="bg-gray-50 border-b border-gray-100 px-2.5 pt-2 pb-2">
                                    <div class="flex items-center gap-2">
                                      <label class="relative flex-shrink-0 cursor-pointer">
                                        <input type="checkbox" v-model="showOptimizedPreview" class="sr-only" />
                                        <div class="relative w-10 h-5 rounded-full transition-colors duration-200"
                                             :class="showOptimizedPreview ? 'bg-securdia-orange' : 'bg-primary-400'">
                                          <div class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200"
                                               :class="showOptimizedPreview ? 'translate-x-5' : 'translate-x-0'"></div>
                                        </div>
                                      </label>
                                      <span class="text-xxs font-semibold text-primary-600">
                                        {{ !showOptimizedPreview ? $t('trip_planner.see_optimized_on_map') : $t('trip_planner.see_original_on_map') }}
                                      </span>
                                    </div>
                                  </div>

                                  <!-- Already optimal: simple current-route info, no comparison rows -->
                                  <div v-if="optimizationResult.distanceSaved <= 0" class="bg-gray-50 p-3 text-center">
                                    <p class="text-xs text-gray-500 mb-2">{{ $t('trip_planner.route_already_optimal_hint') }}</p>
                                    <div class="flex justify-center gap-4 text-xs text-gray-700">
                                      <span><span class="font-bold text-securdia-dark-blue">{{ (originalDistance / 1000).toFixed(1) }} km</span> &nbsp;{{ $t('trip_planner.distance') }}</span>
                                      <span><span class="font-bold text-securdia-dark-blue">{{ formatDuration(originalDuration) }}</span> &nbsp;{{ $t('trip_planner.driving_time') }}</span>
                                    </div>
                                    <button @click="discardOptimization" class="mt-3 text-xs text-gray-400 hover:text-gray-600 underline">{{ $t('trip_planner.discard') }}</button>
                                  </div>

                                  <!-- Metrics grid (only when there are real savings) -->
                                  <div v-if="optimizationResult.distanceSaved > 0" class="p-2.5">
                                    <div class="grid grid-cols-2 gap-2 mb-2.5">
                                      <div class="bg-white rounded p-2 border">
                                        <div class="text-xxs text-gray-400 mb-1">{{ $t('trip_planner.distance') }}</div>
                                        <div class="text-xxs line-through text-gray-400">{{ (originalDistance / 1000).toFixed(1) }} km</div>
                                        <div class="text-sm font-bold text-securdia-dark-blue">{{ (optimizationResult.optimizedDistance / 1000).toFixed(1) }} km</div>
                                        <div class="text-xxs text-securdia-orange mt-0.5">{{ $t('trip_planner.save') }} {{ (optimizationResult.distanceSaved / 1000).toFixed(1) }} km</div>
                                      </div>
                                      <div class="bg-white rounded p-2 border">
                                        <div class="text-xxs text-gray-400 mb-1">{{ $t('trip_planner.driving_time') }}</div>
                                        <div class="text-xxs line-through text-gray-400">{{ formatDuration(originalDuration) }}</div>
                                        <div class="text-sm font-bold text-securdia-dark-blue">{{ formatDuration(optimizationResult.optimizedDuration) }}</div>
                                        <div class="text-xxs text-securdia-orange mt-0.5">{{ $t('trip_planner.save') }} {{ Math.round(optimizationResult.timeSaved / 60) }} min</div>
                                      </div>
                                    </div>

                                    <!-- Action buttons -->
                                    <div class="flex items-center gap-2">
                                      <button
                                        @click="applyOptimizedOrder"
                                        class="flex-1 flex items-center justify-center gap-1 py-1.5 rounded bg-primary-500 hover:bg-primary-600 text-white text-xs font-bold transition-all cursor-pointer">
                                        <BaseIcon icon="check" />
                                        {{ $t('trip_planner.apply_optimized_order') }}
                                      </button>
                                      <button
                                        @click="discardOptimization"
                                        class="text-xs text-gray-400 hover:text-gray-600 px-2 py-1.5 underline">
                                        {{ $t('trip_planner.discard') }}
                                      </button>
                                    </div>
                                  </div>
                                </div>

                              </div>
                              <!-- ── /Optimize Stop Order ──────────────────────── -->
                          </div>
                      </transition>

                      <!-- STEP 3: Schedule -->
                      <transition name="fade" mode="out-in">
                          <div v-if="currentStep === 3" key="step3">
                            <!-- Route Settings -->
                            <div class="mb-4 py-3 border-b">
                              <h4 class="text-sm font-semibold text-gray-700 mb-3">{{ $t('trip_planner.route_settings') }}</h4>
                              
                              <div class="mb-3">
                                <label class="text-xs text-gray-600 block mb-1">{{ $t('trip_planner.deviation_buffer') }}</label>
                                <div class="flex items-center gap-2">
                                  <input v-model.number="bufferZone" type="number" min="50" max="5000" step="50" class="border rounded p-2 w-24 text-sm" />
                                  <span class="text-sm text-gray-600">{{ $t('trip_planner.meters_from_route') }}</span>
                                </div>
                                <p class="text-xs text-gray-500 mt-1">{{ $t('trip_planner.deviation_alert') }}</p>
                              </div>

                              <div class="mb-3">
                                <label class="text-xs text-gray-600 block mb-1">{{ $t('trip_planner.max_waiting_time') }}</label>
                                <div class="flex items-center gap-2">
                                  <input v-model.number="maxWaitingTime" type="number" min="5" max="120" step="5" class="border rounded p-2 w-24 text-sm" />
                                  <span class="text-sm text-gray-600">{{ $t('trip_planner.minutes') }}</span>
                                </div>
                                <p class="text-xs text-gray-500 mt-1">{{ $t('trip_planner.max_waiting_hint') }}</p>
                              </div>
                            </div>
                            <div class="mt-4">
                              <div class="mt-3">
                                <h4 class="text-sm font-semibold text-gray-600 mb-2">{{ $t('trip_planner.timeline_preview') }}</h4>
                                <div class="route-summary overflow-hidden border rounded bg-gray-50 p-2">
                                  <RoutePreviewTimeline :showStats="true" :totalBreakTime="totalBreakTime" :waypoints="destinations" />
                                </div>
                              </div>
                            </div>
                          </div>
                      </transition>

                      <!-- STEP 4: Notifications -->
                      <transition name="fade" mode="out-in">
                        <div v-if="currentStep === 4" key="step4">
                          <div class="mt-4">
                            <div class="mb-4">
                              <label class="text-sm font-semibold text-gray-600 block mb-2">{{ $t('trip_planner.departure_time') }}</label>
                                <date-picker v-model="departureTime" type="datetime" :open.sync="datePickerOpen" :editable="false" :clearable="false" format="YYYY-MM-DD, HH:mm" :minute-step="15" :disabled-date="disableDate" :disabled-time="disableTime" @change="handleTimeSelection" class="w-full"></date-picker>
                                
                                <div class="mt-3 flex flex-wrap gap-2">
                                  <button @click="setQuickTime(0)" class="btn-secondary-outline text-xs px-3 py-1 bg-white hover:bg-gray-50">{{ $t('trip_planner.now') }}</button>
                                  <button @click="setQuickTime(15)" class="btn-secondary-outline text-xs px-3 py-1 bg-white hover:bg-gray-50">+15min</button>
                                  <button @click="setQuickTime(30)" class="btn-secondary-outline text-xs px-3 py-1 bg-white hover:bg-gray-50">+30min</button>
                                  <button @click="setQuickTime(60)" class="btn-secondary-outline text-xs px-3 py-1 bg-white hover:bg-gray-50">+1hr</button>
                                </div>
                            </div>
                            <div class="bg-blue-50 p-3 rounded mb-4">
                              <div class="flex justify-between items-center">
                                  <span class="text-sm text-blue-800">{{ $t('trip_planner.estimated_arrival') }}</span>
                                  <span class="font-bold text-blue-900">{{ expectedArrival }}</span>
                              </div>
                            </div>

                            <div class="mt-3 pt-3 border-t">
                              <label class="text-xs text-gray-600 block mb-1">{{ $t('trip_planner.save_as_template') }}</label>
                              <div class="flex gap-2">
                                <input v-model="templateName" type="text" :placeholder="$t('trip_planner.template_name_placeholder')" class="border rounded p-2 w-full text-sm" @keyup.enter="saveTemplate" />
                                <button class="btn-blue px-3 text-sm whitespace-nowrap" @click.prevent="saveTemplate" :disabled="!canSaveTemplate">
                                  <BaseIcon icon="save" class="mr-1" />{{ $t('user_actions.save') }}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </transition>
                  </div>

                  <!-- WIZARD NAVIGATION (Bottom of sidebar) -->
                  <div class="wizard-footer absolute bottom-0 left-0 w-full bg-white border-t border-gray-200 py-3 flex justify-between z-10">
                      <button 
                          @click="prevStep" 
                          :disabled="currentStep === 1" 
                          class="px-4 py-2 text-sm rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed">
                          {{ $t('user_actions.back') || 'Back' }}
                      </button>
                      
                      <button 
                          v-if="currentStep < totalSteps" 
                          @click="nextStep" 
                          :disabled="!isStepValid" 
                          class="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all">
                          {{ $t('user_actions.next') || 'Next' }}
                          <BaseIcon icon="arrow-right" class="ml-1 text-xs" />
                      </button>
                      
                      <!-- Placeholder for spacing when no Next button (step 4) -->
                      <div v-if="currentStep === totalSteps"></div>
                  </div>

              </div>
              <!-- WIZARD END -->
              <!-- show in only view one -->
              <div v-if="!renderDirection">
                <div class="w-full mt-5 ">
                  <div class="flex justify-between">
                    <div class="text-sm">
                      {{ $t('trip_planner.departure_time') }}
                    </div>
                    <div class="text-sm">
                      {{ formatDate(departureTime) }}
                    </div>
                  </div>

                  <div class="flex justify-between mt-1">
                    <div class="text-sm">
                      {{ $t('trip_planner.estimated_arrival') }}
                    </div>
                    <div class="text-sm">
                      {{ expectedArrival }}
                    </div>
                  </div>

                  <div class="flex justify-between">
                    <div class="text-sm">
                      {{ $t('trip_planner.trip_start') }}
                    </div>
                    <div class="text-sm font-bold">
                      {{ tripStartTime }}
                    </div>
                  </div>

                  <div class="flex justify-between mt-1">
                    <div class="text-sm">
                      {{ $t('trip_planner.trip_end') }}
                    </div>
                    <div class=" text-sm font-bold">
                      {{ tripEndTime }}
                    </div>
                  </div>

                  <div class="border-b border-secondary-400 my-5"></div>

                  <div class="flex justify-between mt-1 max-h-72 overflow-y-scroll overflow-x-hidden">
                    <RoutePreviewTimeline :showStats="true" :totalBreakTime="totalBreakTime" :waypoints="destinations" customClass="completed-route-preview" />
                  </div>

                  <div class="border-b border-secondary-400 my-5"></div>

                  <div class="max-h-72 overflow-y-scroll">
                    <div class="flex flex-col">
                      <div class="text-sm font-bold mb-3">{{ $t('trip_planner.trip_summary') }}</div>
                      <div v-for="(l, i) in completeTripLog" :key="i" @click="showTripReport(l)" class="mt-2 cursor-pointer flex flex-col">
                        <div class="clear-both">
                          <div class="float-left text-sm">
                            <BaseIcon
                              icon="dot-circle"
                              class="mr-2"
                              v-bind:class="{
                                'text-secondary-500': l.type == 'trip_start' || l.type == 'trip_end',
                                'text-yellow-400': l.type == 'waiting_start' || l.type == 'waiting_end',
                                'text-red-500': l.type == 'deviated',
                                'text-accent-500': l.type == 'on_track',
                              }"
                            />
                            <span v-if="l.type == 'trip_start'">{{ $t('trip_planner.trip_started') }}</span>
                            <span v-if="l.type == 'trip_end'">{{ $t('trip_planner.trip_completed') }}</span>
                            <span v-if="l.type == 'break_started'">{{ $t('trip_planner.break_started') }}</span>
                            <span v-if="l.type == 'break_ended'">
                              {{ $t('trip_planner.break_ended') }}<span v-if="l.plannedDuration != undefined">, {{ l.plannedDuration }} min.</span>
                            </span>
                            <span v-if="l.type == 'back_on_route'">{{ $t('trip_planner.on_route') }}</span>
                            <span v-if="l.type == 'deviated_from_route'">{{ $t('trip_planner.deviated') }}</span>
                          </div>
                          <div class="float-right text-sm">
                            {{ formatDate(l.time) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="border-b border-secondary-400 my-5"></div>
                 
                </div>
              </div>
            </div>
          </div>
        </ValidationObserver>
      </div>
      <div class="modal-footer" v-if="renderDirection">
        <div class="footer-container clearfix">
          <button class="btn-red-outline px-3 py-2 float-left" @click="hide()">{{ $t("user_actions.cancel") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
          <button class="btn-blue px-3 py-2 float-right" @click="saveAssetTrip()" v-if="currentStep === totalSteps" :disabled="!isFormValid">{{ $t("user_actions.save") }}<BaseIcon icon="save" class="ml-1" /></button>
        </div>
      </div>
    </modal>
    <TripShareModal />
  </div>
</template>

<script>
import Treeselect from "@riophae/vue-treeselect";
import TripShareModal from "@/components/modal/secure_route/trip_share_modal";
import RoutePreviewTimeline from "@/components/secure_route/route_preview_timeline";
const MODAL_WIDTH = 1500;

import DatePicker from "vue2-datepicker";
import "vue2-datepicker/locale/sv";
import "vue2-datepicker/index.css";
import { gmapApi } from "vue2-google-maps";
import DirectionsRenderer from "@/components/map/DirectionsRenderer";
import draggable from "vuedraggable";
import moment from 'moment-timezone';

export default {
  name: "TripTracker",

  components: {
    DatePicker,
    DirectionsRenderer,
    Treeselect,
    draggable,
    TripShareModal,
    RoutePreviewTimeline
  },

  props: {
    gpss: {
      required: true,
      default: null,
    },
    updateInfo: {
      default: null,
    },
  },

  data() {
    return {
      zoom: this.$store.state.mapZoom,
      center: { lat: 59.33, lng: 18.07 }, // Default to Stockholm
      isEditMode: false,
      selectedAlarm: null,
      datePickerOpen: false,
      updatedWayouts: [],
      // autocomplete
      startLocation: null,
      endLocation: null,
      startMarkerVisible: true,
      endMarkerVisible: true,
      destinations: [{ address: "", type: "destination", breakDuration: 0, breakType: null }],
      breakTypes: [
        { value: 'rest', label: 'trip_planner.break_rest', icon: 'bed' },
        { value: 'meal', label: 'trip_planner.break_meal', icon: 'utensils' },
        { value: 'fuel', label: 'trip_planner.break_fuel', icon: 'gas-pump' },
        { value: 'other', label: 'trip_planner.break_other', icon: 'clock' }
      ],
      // backend
      startPosition: null,
      endPosition: null,
      departureTime: null,
      maxWaitingTime: 30,
      maxStops: 0,
      bufferZone: 300, // Default 300 meters deviation buffer
      routeName: '',
      // drivingOptions: null,
      definedRoute: [],
      waypoints: [],
      savedWaypoints: [],
      totalDistance: null,
      totalDuration: null,
      startAddress: "",
      endAddress: "",
      expectedArrival: "",
      emailAddress: "",
      emailError: "",
      smsNumber: "",
      renderDirection: true,
      trackedRoute: [],
      waitingCircles: [],
      // info window
      infoWindowPos: null,
      infoWinOpen: false,
      infoOptions: { content: "", icon: "", pixelOffset: { width: 0, height: -35 } },
      selectedUnits: null,
      isAlarmSelectDisabled: false,
      drag: false,
      listRenderKey: 0,
      isDestinationInputVisible: false,
      currentStep: 1,
      totalSteps: 4,
      // Template management
      templates: [],
      templateName: '',
      showTemplateList: false,
      completeTripLog: [],
      pinnedTrackedPointInfo: false, // Track if info window is pinned by click
      // Route optimization
      optimizeRoute: false,
      originalDistance: null,
      originalDuration: null,
      optimizationResult: null,
      optimizedPath: [],           // plain {lat,lng} points for the orange polyline
      showOptimizedPreview: true,   // toggle: true=show optimized on map, false=show original
    };
  },
  
  computed: {
    stepTitle() {
      switch(this.currentStep) {
        case 1: return this.$t('trip_planner.step_1_title');
        case 2: return this.$t('trip_planner.step_2_title');
        case 3: return this.$t('trip_planner.step_3_title');
        case 4: return this.$t('trip_planner.step_4_title');
        default: return '';
      }
    },
    isStepValid() {
      if (this.currentStep === 1) {
        return this.routeName.trim() !== '' && !!this.selectedAlarm;
      }
      if (this.currentStep === 2) {
        // Must have at least start and end (which are usually auto-added to destinations array or managed via start/end vars)
        // logic seems to be: destinations[0] is start (if address set)
        return this.destinations.length >= 2 && 
               this.startLocation !== null && 
               this.endLocation !== null; 
      }
      if (this.currentStep === 3) {
        return this.bufferZone !== '' && this.bufferZone >= 0
      }
      if (this.currentStep === 4) {
        // Valid if email is valid (if provided) and phone is valid (if provided)
        // Actually the original 'isFormValid' handles this? Let's check original computed.
        return !!this.departureTime;
      }
      return false;
    },
    google: gmapApi,
    totalBreakTime() {
      return this.destinations
        .filter(d => d.type === 'break')
        .reduce((sum, d) => sum + (d.breakDuration || 0), 0);
    },
    tripStartTime() {
      if (this.updateInfo && this.updateInfo.trip_log.length > 0) {
        let l = _.find(this.updateInfo.trip_log, { type: "trip_start" });
        if (l) return this.formatDate(l.time);
      }
      return "";
    },
    tripEndTime() {
      if (this.updateInfo && this.updateInfo.trip_log.length > 0) {
        let l = _.find(this.updateInfo.trip_log, { type: "trip_end" });
        if (l) return this.formatDate(l.time);
      }
      return "";
    },
    tripTotalWaitingTime() {
      if (this.updateInfo && this.updateInfo.trip_log.length > 0) {
        let waitEndedArr = _.filter(this.updateInfo.trip_log, { type: "break_ended" });
        if (waitEndedArr.length > 0) {
          let t = 0;
          for (let i = 0; i < waitEndedArr.length; i++) {
            let w = waitEndedArr[i];
            t += w.duration;
          }

          return t;
        }
      }
      return 0;
    },
    dragOptions() {
      return {
        animation: 200,
        group: "description",
        disabled: false,
        ghostClass: "ghost",
      };
    },
    isFormValid() {
      // Check required fields for trip creation
      return (
        this.routeName.trim() !== '' &&
        this.selectedAlarm != null &&
        this.startLocation != null &&
        this.endLocation != null &&
        this.departureTime != null
      );
    },
    canSaveTemplate() {
      return this.templateName.trim() !== '' && 
             this.startLocation !== null && 
             this.endLocation !== null &&
             this.destinations.length >= 2;
    },
    // True when there are ≥ 3 located destination stops (start + ≥1 middle + end)
    hasOptimizableStops() {
      return this.destinations.filter(d => d.type === 'destination' && d.location).length >= 3;
    },
  },

  watch: {
    // departureTime: function () {
    //   this.debounceDrivingOptions();
    // },
    updateInfo: {
      handler(newVal) {
        if (newVal) {
          this.setupTripInfo();
          this.isEditMode = true;
        }else {
          this.isEditMode = false;
        }
      },
      deep: true,
      immediate: false
    },
    destinations: {
      handler() {
        this.calculateExpectedArrival();
      },
      deep: true
    },
    currentStep(newStep) {
      // Repopulate autocomplete inputs when returning to step 2
      if (newStep === 2) {
        this.$nextTick(() => {
          this.rearrangeAdressInput();
        });
      }
    }
  },

  methods: {
    nextStep() {
      if (this.currentStep < this.totalSteps && this.isStepValid) {
          this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
          this.currentStep--;
      }
    },
    setStartPlace(place) {
      this.waypoints = [];
      this.startLocation = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      //console.log("🚀 ~ file: startLocation:", this.startLocation);
      this.focusMarker(this.startLocation);
      this.addDestination();
    },

    setEndPlace(place) {
      this.waypoints = [];

      this.endLocation = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      this.focusMarker(this.endLocation);
    },

    setStopsPlace(place, index) {
      const waypoint = {
        location: {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        },
        type: "destination",
        stopover: true,
        address: place.name + " | " + place.formatted_address,
      };

      this.destinations[index] = waypoint;

      if (this.destinations[0].type === 'destination') {
        this.startLocation = this.destinations[0].location;
      }

      if (this.destinations[this.destinations.length - 1].type === 'destination' && index > 0) {
        this.endLocation = this.destinations[this.destinations.length - 1].location;
      }

      this.setWayPoints();

      if (index === 0 && this.destinations.length === 1) {
        this.addDestination();
      }

      this.focusMarker(waypoint.location);
    },
    addDestination() {
      const destinationCount = this.destinations.filter(d => d.type === 'destination').length;
      if (destinationCount >= 5) {
        this.handleError(null, this.$t('trip_planner.max_destinations_reached'));
        return;
      }
      this.destinations.push({ address: "", type: "destination", breakDuration: 0, breakType: null });
      this.isDestinationInputVisible = false;
    },
    canAddBreakAt(index) {
      // only allow adding a break between two valid destination items
      if (index === null || index === undefined) return false;
      if (index <= 0) return false; // do not allow button on first input
      if (index >= this.destinations.length - 1) return false; // do not allow on last input

      const left = this.destinations[index];
      const right = this.destinations[index + 1];
      if (!left || !right) return false;
      if (left.type === 'break' || right.type === 'break') return false; // gap already has a break
      return true;
    },

    addBreak(afterIndex = null) {
      // validate allowed gap
      if (!this.canAddBreakAt(afterIndex)) return;

      const breakItem = {
        address: "",
        type: "break",
        breakDuration: 30,
        breakType: 'meal',
        note: '',
        location: null
      };

      // Insert break after the specified index
      this.destinations.splice(afterIndex + 1, 0, breakItem);

      this.isDestinationInputVisible = false;
      this.calculateExpectedArrival();

      // Repopulate autocomplete inputs after DOM updates
      this.$nextTick(() => {
        setTimeout(() => {
          this.rearrangeAdressInput();
        }, 100);
      });
    },
    removeDestination(index) {
      //console.log("🚀 ~ removeDestination ~ index:", index);

      this.destinations.splice(index, 1);
      let vm = this;
      setTimeout(() => {
        // Find first and last valid destination for start/end locations
        const firstDest = vm.destinations.find(d => d.type === 'destination' && d.location);
        const lastDest = [...vm.destinations].reverse().find(d => d.type === 'destination' && d.location);
        
        if (firstDest) vm.startLocation = firstDest.location;
        if (lastDest) vm.endLocation = lastDest.location;
      }, 500);

      this.setWayPoints();
      this.calculateExpectedArrival();
    },
    onDragEnd() {
      // Find first and last valid destination for start/end locations
      const firstDest = this.destinations.find(d => d.type === 'destination' && d.location);
      const lastDest = [...this.destinations].reverse().find(d => d.type === 'destination' && d.location);
      
      if (firstDest) this.startLocation = firstDest.location;
      if (lastDest) this.endLocation = lastDest.location;

      this.setWayPoints();
      setTimeout(() => {
        this.rearrangeAdressInput();
      }, 300);
    },
    setWayPoints() {
      let vm = this;
      this.$nextTick(() => {
        // Filter out breaks or invalid destinations
       const wayStops = vm.destinations
          .filter(d => d.type === 'destination' && d.location)
          .slice(1, -1)
    
        const cleanWayStops =JSON.parse(JSON.stringify(wayStops));
        cleanWayStops.forEach((item) => {
          delete item.address;
          delete item.type;
        });
        vm.waypoints = cleanWayStops;

        // Also update savedWaypoints for backend save
        vm.savedWaypoints = vm.destinations;

      });
    },

    rearrangeAdressInput() {
      let vm = this;
      this.listRenderKey++;
      // console.log("🚀 ~ rearrangeAdressInput ~ vm.destinations:");
      this.$nextTick(() => {
        vm.destinations.forEach((des, i) => {
          // Skip breaks - they don't have autocomplete inputs
          if (des.type === 'break') return;
          
          let autocompleteRef = vm.$refs["autocomplete-" + i];
          // console.log("🚀 ~ rearrangeAdressInput:");
          if (autocompleteRef && autocompleteRef[0] && autocompleteRef[0].$el) {
            vm.$refs["autocomplete-" + i][0].$el.value = des.address;
          }
        });
      });
    },

    focusMarker(position) {
      this.$refs.map.$mapObject.panTo(position);
      this.$refs.map.$mapObject.setZoom(15);
    },

    async changeLocationStart(event) {
      try {
        let position = { lat: event.latLng.lat(), lng: event.latLng.lng() };

        const geocoder = new this.google.maps.Geocoder();
        let response = await geocoder.geocode({ location: position });

        this.$refs.startPlace.$refs.input.value = response.results[0].formatted_address;
      } catch (error) {
        console.log(error);
      }
    },

    async changeLocationEnd(event) {
      try {
        let position = { lat: event.latLng.lat(), lng: event.latLng.lng() };

        const geocoder = new this.google.maps.Geocoder();
        let response = await geocoder.geocode({ location: position });

        this.$refs.endPlace.$refs.input.value = response.results[0].formatted_address;
      } catch (error) {
        console.log(error);
      }
    },

    directionChanged(data) {
      // console.log("🚀 ~ data:", data)
      this.focusMarker({ lat: 23.8758547, lng: 90.3795438 });

      this.startMarkerVisible = false;
      this.endMarkerVisible = false;

      // Sum all legs for routes with multiple waypoints
      let totalDuration = 0;
      let totalDistance = 0;
      
      data.routes[0].legs.forEach(leg => {
        totalDuration += leg.duration.value;  // seconds
        totalDistance += leg.distance.value;  // meters
      });
      
      this.totalDuration = totalDuration;
      this.totalDistance = totalDistance;

      // If route changes while comparison is active, discard stale optimized data
      if (this.optimizedPath.length > 0 || this.optimizeRoute) {
        this.optimizedPath = [];
        this.optimizeRoute = false;
        this.optimizationResult = null;
      }
      // Always update baseline metrics
      this.originalDistance = totalDistance;
      this.originalDuration = totalDuration;

      // Get start from first leg, end from last leg
      const firstLeg = data.routes[0].legs[0];
      const lastLeg = data.routes[0].legs[data.routes[0].legs.length - 1];
      
      this.startPosition = { lat: firstLeg.start_location.lat(), lng: firstLeg.start_location.lng() };
      this.startAddress = firstLeg.start_address;

      this.endPosition = { lat: lastLeg.end_location.lat(), lng: lastLeg.end_location.lng() };
      this.endAddress = lastLeg.end_address;

      //TODO: check how the data is set
      //overview_path
      this.definedRoute = data.routes[0].overview_path;

      let waypoints = [];
      // Collect all via waypoints from all legs
      data.routes[0].legs.forEach(leg => {
        for (let i = 0; i < leg.via_waypoints.length; i++) {
          let w = leg.via_waypoints[i];
          waypoints.push({ lat: w.lat(), lng: w.lng() });
        }
      });
      
      // this.savedWaypoints = waypoints;
      this.calculateExpectedArrival();
      // this.$emit('routeChanged', this.definedRoute);

    },

    fetchOptimizedRoute() {
      if (!this.startLocation || !this.endLocation || !this.google) return;
      // Snapshot the current rendered route as the baseline RIGHT NOW (before the async call),
      // so the callback always compares against the route the user was looking at when they clicked.
      const baselineDistance = this.totalDistance || 0;
      const baselineDuration = this.totalDuration || 0;
      this.originalDistance = baselineDistance;
      this.originalDuration = baselineDuration;
      this.optimizeRoute = true;    // show loading spinner
      this.optimizationResult = null;
      this.optimizedPath = [];
      this.showOptimizedPreview = true;

      const directionsService = new this.google.maps.DirectionsService();
      directionsService.route(
        {
          origin: this.startLocation,
          destination: this.endLocation,
          waypoints: this.waypoints,
          travelMode: this.google.maps.TravelMode.DRIVING,
          optimizeWaypoints: true,
        },
        (response, status) => {
          if (status !== 'OK') {
            this.handleError(null, 'Could not optimize route: ' + status);
            this.optimizeRoute = false;
            return;
          }

          let totalDuration = 0;
          let totalDistance = 0;
          response.routes[0].legs.forEach(leg => {
            totalDuration += leg.duration.value;
            totalDistance += leg.distance.value;
          });

          const waypointOrder = response.routes[0].waypoint_order || [];
          const distanceSaved = baselineDistance - totalDistance;
          const timeSaved = baselineDuration - totalDuration;
          const efficiencyGain = baselineDistance > 0
            ? (distanceSaved / baselineDistance) * 100 : 0;

          this.optimizationResult = {
            optimizedDistance: totalDistance,
            optimizedDuration: totalDuration,
            waypointOrder,
            distanceSaved,
            timeSaved,
            efficiencyGain: Math.max(0, efficiencyGain),
          };

          // Only draw the orange comparison polyline when there are real savings.
          // If distanceSaved <= 0 the route is already optimal — keep optimizedPath empty
          // so the orange GmapPolyline stays hidden, the blue line stays at full opacity,
          // and the map legend is not shown.
          if (distanceSaved > 0) {
            this.optimizedPath = response.routes[0].overview_path.map(p => ({
              lat: p.lat(),
              lng: p.lng(),
            }));
          } else {
            this.optimizedPath = [];
          }
        }
      );
    },

    calculateExpectedArrival() {
      const destinationStops = this.destinations.filter(d => d.type === 'destination');
      const actualStopsCount = Math.max(0, destinationStops.length - 2);
      this.maxStops = actualStopsCount;
        
      if (this.totalDuration) {
        // Calculate total break time
        let totalBreakTime = this.destinations
          .filter(d => d.type === 'break')
          .reduce((sum, d) => sum + (d.breakDuration || 0), 0);
        
        // Calculate total waiting time based on actual stops
        let totalWaitingTime = parseInt(this.maxWaitingTime) * this.maxStops;
        
        this.expectedArrival = this.moment(this.departureTime)
          .add(totalBreakTime, "minutes")
          .add(totalWaitingTime, "minutes")
          .add(this.totalDuration, "seconds")
          .format("YYYY-MM-DD, HH:mm");
      }
    },

    applyOptimizedOrder() {
      if (!this.optimizationResult || !this.optimizationResult.waypointOrder) return;

      const order = this.optimizationResult.waypointOrder;

      // Extract destination-type stops only (not breaks)
      const destItems = this.destinations.filter(d => d.type === 'destination');
      const start = destItems[0];
      const end = destItems[destItems.length - 1];
      const midDestinations = destItems.slice(1, -1);

      // Reorder middle stops according to the optimized order
      const reorderedMids = order.map(i => midDestinations[i]);

      // Rebuild destinations: start + reordered mids + end (breaks are dropped as stop order changes)
      this.destinations = [start, ...reorderedMids, end];

      // Apply optimized metrics as the new baseline
      this.originalDistance = this.optimizationResult.optimizedDistance;
      this.originalDuration = this.optimizationResult.optimizedDuration;
      this.totalDistance = this.optimizationResult.optimizedDistance;
      this.totalDuration = this.optimizationResult.optimizedDuration;

      // Reset optimization UI state
      this.optimizeRoute = false;
      this.optimizationResult = null;
      this.optimizedPath = [];
      this.showOptimizedPreview = true;

      // Refresh waypoints and autocomplete address inputs
      this.setWayPoints();
      this.$nextTick(() => {
        this.rearrangeAdressInput();
      });

      this.handleSuccess(this.$t('trip_planner.route_optimized'));
    },

    discardOptimization() {
      this.optimizeRoute = false;
      this.optimizationResult = null;
      this.optimizedPath = [];
      this.showOptimizedPreview = true;
    },

    handleDirectionError(status) {
      if (status == "ZERO_RESULTS") {
        this.handleError(null, "Ingen direkt rutt", "top-right", 3000);
        this.startMarkerVisible = true;
        this.endMarkerVisible = true;
      }

      if (status == "INVALID_REQUEST") {
        this.handleError(null, "Avgångstid måste vara i framtiden", "top-right", 1500);
        this.checkDepartureTime();
      }
    },

    copyDirectionLink() {
      var vm = this;
      let origin = `${this.startPosition.lat},${this.startPosition.lng}`;
      let destination = `${this.endPosition.lat},${this.endPosition.lng}`;
      let waypoints = "";
      for (let i = 0; i < this.savedWaypoints.length; i++) {
        let w = this.savedWaypoints[i];
        waypoints += `${w.lat},${w.lng}|`;
      }

      let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving&waypoints=${waypoints}`;

      const cb = navigator.clipboard;
      cb.writeText(url).then(() => vm.handleSuccess("Vägbeskrivning kopierad"));
    },
    async getFormattedAddress(position) {
      try {
        //let position = { lat: location.lat(), lng: event.latLng.lng() };

        const geocoder = new this.google.maps.Geocoder();
        let response = await geocoder.geocode({ location: position });

        return response.results[0].formatted_address;
      } catch (error) {
        console.log(error);
      }
    },
    saveAssetTrip() {
      this.$refs.formAssetTracker.validate().then((success) => {
        if (!success) {
          this.handleError(null, this.$t('trip_planner.required_fields_empty'));
          return;
        }
        const destinationStops = this.destinations.filter(d => d.type === 'destination');
        const actualStopsCount = Math.max(0, destinationStops.length - 2);
        this.maxStops = actualStopsCount;
        // todo: convert time to UTC
        let data = {
          imei_number: this.selectedAlarm,
          start_position: this.startPosition,
          end_position: this.endPosition,
          start_address: this.startAddress,
          end_address: this.endAddress,
          departure_time: this.departureTime,
          waiting_limit: this.maxWaitingTime,
          waiting_stop: this.maxStops,
          buffer_zone: this.bufferZone,
          route_name: this.routeName,
          defined_route: this.definedRoute,
          waypoints: this.savedWaypoints,
          planned_stops: this.destinations,
          total_distance: this.totalDistance,
          total_duration: this.totalDuration,
          email_address: this.emailAddress,
          sms_number: this.smsNumber,
        };

        if (this.updateInfo != null) {
          delete data.imei_number;

          this.axios
            .put(`${process.env.VUE_APP_SERVER_URL}/asset-tracker/trip/${this.updateInfo._id}/${this.imei_number}`, data)
            .then(() => {
              //eslint-disable-line
              this.$emit("routeSaved");
              this.hide();
              this.handleSuccess();
              this.reset();
            })
            .catch((error) => {
              this.handleError(error);
            });
        } else {
          this.axios
            .post(`${process.env.VUE_APP_SERVER_URL}/asset-tracker`, data)
            .then(() => {
              //eslint-disable-line
              this.$emit("routeSaved");
              this.hide();
              this.handleSuccess();
              this.reset();
            })
            .catch((error) => {
              this.handleError(error);
            });
        }
      });
    },

    disableDate(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    },

    validateEmail() {
      if (!this.emailAddress) {
        this.emailError = "";
        return true;
      }
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.emailAddress)) {
        this.emailError = this.$t('trip_planner.invalid_email');
        return false;
      }
      
      this.emailError = "";
      return true;
    },

    setQuickTime(minutesFromNow) {
      const now = this.moment();
      now.add(minutesFromNow, 'minutes');
      
      // Round to nearest 15 minutes
      const remainder = now.minutes() % 15;
      if (remainder !== 0) {
        now.add(15 - remainder, 'minutes');
      }
      
      this.departureTime = new Date(now);
      this.calculateExpectedArrival();
    },

    disableTime(date) {
      let today = this.moment();
      today.subtract(1, "hour");
      return date < new Date(today);
    },

    handleTimeSelection(value, type) {
      // eslint-disable-line
      if (type === "minute") {
        this.datePickerOpen = false;
      }

      this.checkDepartureTime();
      this.calculateExpectedArrival();
    },

    checkDepartureTime() {
      let departureTimeMoment = this.moment(this.departureTime);
      let now = this.moment();
      if (departureTimeMoment.isBefore(now)) {
        let t = 15 - (now.minutes() % 15);
        now.add(t, "minutes");
        this.departureTime = new Date(now);
      }
    },

    initDate() {
      // Smart default: Set to next 15-minute interval
      const now = this.moment();
      const minutes = Math.ceil(now.minutes() / 15) * 15;
      now.minutes(minutes);
      now.seconds(0);
      
      this.departureTime = new Date(now);

      this.destinations = [{ address: "", type: "destination", breakDuration: 0, breakType: null }];
      this.selectedAlarm = null;
      this.$store.commit("SET_OPTIMIZED_PATH", []);
      
      // Auto-fill email from user profile if available
      if (this.$store.state.user && this.$store.state.user.email && !this.emailAddress) {
        this.emailAddress = this.$store.state.user.email;
      }
    },

    setupTripInfo() {
      var vm = this;
      if (this.updateInfo) {
        // Check if trip has started
        if (this.updateInfo.trip_state && this.updateInfo.trip_state.tripStarted) {
          this.renderDirection = false;
        }

        // Step 1: Alarm Selection
        this.selectedAlarm = this.updateInfo.imei_number;
        this.isAlarmSelectDisabled = true;
        
        // Trigger device selection to center map
        this.$nextTick(() => {
          this.deviceSelected(this.selectedAlarm);
        });

        // Step 2: Route Planning - Populate destinations with breaks
        if (this.updateInfo.waypoints && this.updateInfo.waypoints.length > 0) {
          this.destinations = JSON.parse(JSON.stringify(this.updateInfo.waypoints));
          this.isDestinationInputVisible = true;
          
          // Set start and end locations from destinations
          const firstDest = this.destinations.find(d => d.type === 'destination' && d.location);
          const lastDest = [...this.destinations].reverse().find(d => d.type === 'destination' && d.location);
          
          if (firstDest) this.startLocation = firstDest.location;
          if (lastDest) this.endLocation = lastDest.location;

          // Repopulate address inputs after DOM update
          setTimeout(() => {
            vm.rearrangeAdressInput();
            vm.setWayPoints();
          }, 300);
        }

        // Populate addresses
        if (this.updateInfo.start_address) this.startAddress = this.updateInfo.start_address;
        if (this.updateInfo.end_address) this.endAddress = this.updateInfo.end_address;
        
        // Populate positions
        if (this.updateInfo.start_position) {
          this.startPosition = this.updateInfo.start_position;
          if (!this.startLocation) this.startLocation = this.updateInfo.start_position;
        }
        if (this.updateInfo.end_position) {
          this.endPosition = this.updateInfo.end_position;
          if (!this.endLocation) this.endLocation = this.updateInfo.end_position;
        }

        // Populate route calculation data
        if (this.updateInfo.total_duration) this.totalDuration = this.updateInfo.total_duration;
        if (this.updateInfo.total_distance) this.totalDistance = this.updateInfo.total_distance;
        if (this.updateInfo.waiting_limit) this.maxWaitingTime = this.updateInfo.waiting_limit;
        if (this.updateInfo.waiting_stop) this.maxStops = this.updateInfo.waiting_stop;
        if (this.updateInfo.buffer_zone) this.bufferZone = this.updateInfo.buffer_zone;
        if (this.updateInfo.route_name) this.routeName = this.updateInfo.route_name;
        

        // Populate route path
        if (this.updateInfo.defined_route) this.definedRoute = this.updateInfo.defined_route;

        // Step 3: Departure Time
        if (this.updateInfo.departure_time) {
          this.departureTime = new Date(this.updateInfo.departure_time);
         
        }

        // Step 4: Notifications
        if (this.updateInfo.email_address) this.emailAddress = this.updateInfo.email_address;
        if (this.updateInfo.sms_number) this.smsNumber = this.updateInfo.sms_number;

        // Populate tracking data for active trips
        if (this.updateInfo.tracked_route && this.updateInfo.tracked_route.length > 0) {
          this.trackedRoute = this.updateInfo.tracked_route;
        }

        if (this.updateInfo.trip_log && this.updateInfo.trip_log.length > 0) {
          let waitingPositions = _.filter(this.updateInfo.trip_log, { type: "break_started" });
          this.waitingCircles = waitingPositions.map(w => w.position);
        }

        this.completeTripLog = this.updateInfo.trip_log?.filter(log => ["trip_start","deviated_from_route","break_started","break_ended","back_on_route","trip_end"].includes(log.type)) || [];

        // Set map bounds after all locations are set
          this.setMapBounds();
          this.calculateExpectedArrival();
      }
    },

    deviceSelected() {
      if (this.$store.getters.getGpsData.length > 0) {
        this.$store.getters.getGpsData.forEach((gps) => {

          // console.log("🚀 ~ gps.imei_number:", gps.imei_number);
          // console.log("🚀 ~ this.selectedAlarm:", this.selectedAlarm);
          if (gps.imei_number === this.selectedAlarm) {
            //const alarm = this.$store.getters.getAlarms.find((item) => item.imei_number === this.selectedAlarm);
            // this.center = gps.gps_pos;
            let icon = require("@/assets/icon/car.png");
            this.infoOptions.icon = icon;

            let markerObj = {
              //icon: icon,
              position: { lat: gps.gps_pos.lat ? gps.gps_pos.lat : 0, lng: gps.gps_pos.lng ? gps.gps_pos.lng : 0 },
              infoText: ``,
              alarmId: "test", //alarm._id,
              imei: this.selectedAlarm,
            };

            //  `${this.$t("map_info_popup.alarm_name")}: <b>${alarm.unit_name}</b><br>
            //           ${this.$t("map_info_popup.gsm_signal_strength")}: <b>${this.getGSMSignalPercent(gps.gsm_signal, alarm.type)}</b><br>
            //           ${this.$t("map_info_popup.battery_percentage")}: <b>${gps.battery_percent} %</b><br>
            //           ${this.$t("map_info_popup.speed")}: <b>${gps.alarm_speed} km/hr</b><br>
            //           Lat: <b>${gps.gps_pos.lat ? gps.gps_pos.lat.toFixed(3) : "-"}</b>, Lng: <b>${gps.gps_pos.lng ? gps.gps_pos.lng.toFixed(3) : "-"}</b>
            //           `,

            setTimeout(() => {
              this.zoom = 13;
              this.showInfoWindow(gps.gps_pos, markerObj.infoText, true);
            }, 300);
          }
        });
      }
    },
    reset() {
      this.currentStep = 1;
      this.startLocation = null;
      this.endLocation = null;
      this.startPosition = null;
      this.startMarkerVisible = true;
      this.endMarkerVisible = true;
      this.endPosition = null;
      this.departureTime = null;
      this.maxWaitingTime = 30;
      this.maxStops = 0;
      this.bufferZone = 300;
      this.routeName = '';
      this.definedRoute = [];
      this.waypoints = [];
      this.savedWaypoints = [];
      this.totalDistance = null;
      this.totalDuration = null;
      this.startAddress = "";
      this.endAddress = "";
      this.expectedArrival = "";
      this.emailAddress = "";
      this.smsNumber = "";
      this.renderDirection = true;
      this.trackedRoute = [];
      this.waitingCircles = [];
      this.infoWindowPos = null;
      this.infoOptions.content = "";
      this.infoWinOpen = false;
      this.templateName = '';
      this.showTemplateList = false;
      this.optimizeRoute = false;
      this.originalDistance = null;
      this.originalDuration = null;
      this.optimizationResult = null;
      this.optimizedPath = [];
    },

    isNumber(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },

    setMapBounds() {
      var vm = this;

      setTimeout(() => {
        vm.$refs.map.$mapPromise.then((map) => {
          const bounds = new vm.google.maps.LatLngBounds();

          if (vm.startLocation) bounds.extend(vm.startLocation);
          if (vm.endLocation) bounds.extend(vm.endLocation);

          map.fitBounds(bounds);
        });
      }, 500);
    },

    showTripReport(log) {
      let logType = "";
      if (log.type == "trip_start") logType = this.$t('trip_planner.trip_started');
      else if (log.type == "trip_end") logType = this.$t('trip_planner.trip_completed');
      else if (log.type == "break_started") logType = this.$t('trip_planner.break_started');
      else if (log.type == "break_ended") logType = this.$t('trip_planner.break_ended');
      else if (log.type == "back_on_route") logType = this.$t('trip_planner.on_route');
      else if (log.type == "deviated_from_route") logType = this.$t('trip_planner.deviated');

      let infoText = `
        ${logType}<br>
        ${this.formatDate(log.time)}
      `;
      if (log.type == "break_ended") infoText += `<br>${log.plannedDuration} min.`;

      this.showInfoWindow(log.position, infoText, true);
      this.zoomPosition(log.position);
    },

    showInfoWindow(position, infoText, show) {
      this.infoWindowPos = position;
      this.infoOptions.content = infoText;
      this.infoWinOpen = show;
    },

    zoomPosition(position) {
      var vm = this;
      setTimeout(() => {
        vm.$refs.map.$mapObject.panTo(position);
        vm.$refs.map.$mapObject.setZoom(13);
      }, 300);
    },
    getGSMSignalPercent(g, type) {
      if (new RegExp(/^TELTONIKA/g).test(type)) {
        return g * 20 + "%";
      }
      {
        // conversion as per SRT documentation
        if (g <= 31) return Math.ceil(3.225 * g) + " %";
        else return "N/A";
      }
    },
    formatDuration(seconds) {
      return this.moment.duration(seconds, "seconds").humanize();
    },
    // Template management methods
    toggleTemplateList() {
      this.showTemplateList = !this.showTemplateList;
    },
    
    loadTemplates() {
      const customerId = this.$route.params.id;
      if (!customerId) {
        console.warn('No customer ID available');
        return;
      }

      this.axios
        .get(`${process.env.VUE_APP_SERVER_URL}/route-templates/customer/${customerId}`)
        .then((response) => {
          this.templates = response.data?.templates?.map(template => ({
            id: template._id,
            name: template.template_name,
            created: template.createdAt,
            selectedAlarm: template.imei_number,
            destinations: template.planned_stops || [],
            waypoints: template.waypoints || [],
            defined_route: template.defined_route || [],
            maxWaitingTime: template.waiting_limit,
            maxStops: template.waiting_stop,
            bufferZone: template.buffer_zone,
            routeName: template.route_name,
            startPosition: template.start_position,
            endPosition: template.end_position,
            startAddress: template.start_address,
            endAddress: template.end_address,
            totalDistance: template.total_distance,
            totalDuration: template.total_duration,
          })) ?? [];
        })
        .catch(() => {
          this.templates = [];
        });
    },
    
    saveTemplate() {
      if (!this.canSaveTemplate) {
        this.handleError(null, 'Please provide a template name and valid route');
        return;
      }

      const customerId = this.$route.params.id;
      if (!customerId) {
        this.handleError(null, 'Customer ID not found');
        return;
      }
      
      const templateData = {
        customer_id: customerId,
        template_name: this.templateName.trim(),
        route_name: this.routeName || this.templateName.trim(),
        imei_number: this.selectedAlarm,
        start_position: this.startPosition,
        end_position: this.endPosition,
        start_address: this.startAddress,
        end_address: this.endAddress,
        departure_time: this.departureTime,
        waiting_limit: this.maxWaitingTime,
        waiting_stop: this.maxStops,
        buffer_zone: this.bufferZone,
        defined_route: this.definedRoute,
        waypoints: this.savedWaypoints,
        planned_stops: this.destinations,
        total_distance: this.totalDistance,
        total_duration: this.totalDuration,
      };
      
      this.axios
        .post(`${process.env.VUE_APP_SERVER_URL}/route-templates`, templateData)
        .then(() => {
          this.handleSuccess('Template saved successfully');
          this.templateName = '';
          // Reload templates to get the newly saved one
          this.loadTemplates();
        })
        .catch((error) => {
          this.handleError(error, 'Failed to save template');
        });
    },
    
    applyTemplate(index) {
      if (index < 0 || index >= this.templates.length) return;
      
      const template = this.templates[index];

  
      // Apply route data from template
      this.selectedAlarm = template.selectedAlarm || null;
      this.destinations = JSON.parse(JSON.stringify(template.destinations || []));
      this.savedWaypoints = JSON.parse(JSON.stringify(template.waypoints || []));
      this.definedRoute = JSON.parse(JSON.stringify(template.defined_route || []));
      this.maxWaitingTime = template.maxWaitingTime || 30;
      this.maxStops = template.maxStops || 0;
      this.bufferZone = template.bufferZone || 300;
      this.routeName = template.routeName || '';
      
      // Apply positions and addresses from template
      if (template.startPosition) this.startPosition = template.startPosition;
      if (template.endPosition) this.endPosition = template.endPosition;
      if (template.startAddress) this.startAddress = template.startAddress;
      if (template.endAddress) this.endAddress = template.endAddress;
      if (template.totalDistance) this.totalDistance = template.totalDistance;
      if (template.totalDuration) this.totalDuration = template.totalDuration;
      
      // Extract start and end locations from destinations
      if (this.destinations.length > 0) {
        const firstDest = this.destinations.find(d => d.type === 'destination');
        if (firstDest && firstDest.location) {
          this.startLocation = firstDest.location;
        }
        
        const destOnly = this.destinations.filter(d => d.type === 'destination');
        if (destOnly.length > 0) {
          const lastDest = destOnly[destOnly.length - 1];
          if (lastDest.location) {
            this.endLocation = lastDest.location;
          }
        }
      }
      
      this.handleSuccess(`Template "${template.name}" applied`);
      this.showTemplateList = false;
      
      // Trigger device selection if alarm is set
      if (this.selectedAlarm) {
        this.deviceSelected(this.selectedAlarm);
      }
      
      // Force re-render of direction if needed
      setTimeout(() => {
        this.rearrangeAdressInput();
        this.setWayPoints();
      }, 500);

      this.calculateExpectedArrival();
    },
    
    deleteTemplate(index) {
      if (index < 0 || index >= this.templates.length) return;
      
      const template = this.templates[index];
      
      this.$modal.show("dialog", {
        title: `<span class="font-normal text-sm">${this.$t('trip_planner.route_template_delete_dialog_msg', { name: template.name })}</span>`,
        buttons: [
          {
            title: `<div class="bg-accent-500 text-white text-sm font-sans py-2">${this.$t('asset_tracking_table.cancel') || 'Cancel'}</div>`,
            handler: () => {
              this.$modal.hide("dialog");
            },
          },
          {
            title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t('asset_tracking_table.delete') || 'Delete'}</div>`,
            handler: () => {
              this.axios
                .delete(`${process.env.VUE_APP_SERVER_URL}/route-templates/${template.id}`)
                .then(() => {
                  this.handleSuccess('Template deleted');
                  this.loadTemplates();
                  this.$modal.hide("dialog");
                })
                .catch((error) => {
                  console.error('Error deleting template:', error);
                  this.handleError(error, 'Failed to delete template');
                  this.$modal.hide("dialog");
                });
            },
          },
        ],
      });
    },
    showTrackedPointInfo(point, index) {
      let time = point?.timestamp ? moment(point?.timestamp).tz('Europe/Stockholm').format('HH:mm:ss') : '-';
      let speed = point?.timestamp ? `${point?.speed?.toFixed(2)} km/h` : '-';

      // Fallback to GPS speed if available and no log entry speed
      if (speed === '-' && this.gps?.alarm_speed) {
        speed = `${this.gps.alarm_speed} km/h`;
      }

      const infoText = `
        <b>Route Point ${index}</b><br>
        <b>Time: ${time}</b><br>
        <b>Speed: ${speed}</b><br>
        <b>Lat: ${point?.lat?.toFixed(6)}</b><br>
        <b>Lng: ${point?.lng?.toFixed(6)}</b>
      `;
      this.showInfoWindow(point, infoText, true);
    },
    pinTrackedPointInfo(point, index) {
      this.showTrackedPointInfo(point, index);
      this.pinnedTrackedPointInfo = true;
    },  
    closeTrackedPointInfo() {
      this.infoWinOpen = false;
      this.pinnedTrackedPointInfo = false;
    },
    closeTrackedPointInfoUnlessPinned() {
      if (!this.pinnedTrackedPointInfo) {
        this.infoWinOpen = false;
      }
    },
    
    hide() {
      this.$modal.hide("modal-trip-tracker");
    },
  },

  created() {
    this.modalWidth = window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH;
    // this.debounceDrivingOptions = _.debounce(this.setDrivingOptions, 2000);
  },

  mounted() {
    this.initDate();
    this.destinations = [{}];
  },
};
</script>
<style scoped>
.close-icon {
  display: none;
}
.destination:hover .close-icon {
  display: block;
}

.stop-icon {
  display: block;
}
.drag-icon {
  display: none;
}

.destination:hover .stop-icon {
  display: none;
}
.destination:hover .drag-icon {
  display: block;
  cursor: pointer;
}

.button {
  margin-top: 35px;
}

.flip-list-move {
  transition: transform 0.5s;
}

.no-move {
  transition: transform 0s;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.list-group {
  min-height: 20px;
}

.list-group-item {
  cursor: move;
}

.list-group-item i {
  cursor: pointer;
}

/* Fade transition */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.route-tab {
  max-height: 640px;
}
.route-summary {
  max-height: 100%;
}
/* Wizard Transitions */
.fade-enter-active {
  transition: all 0.4s ease-in-out;
}
.fade-leave-active {
  transition: all 0.3s ease-in-out;
}
.fade-enter {
  opacity: 0;
  transform: translateX(10px);
}
.fade-enter-to {
  opacity: 1;
  transform: translateX(0);
}
.fade-leave {
  opacity: 1;
  transform: translateX(0);
}
.fade-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

/* Wizard Styles */
.wizard-footer {
    backdrop-filter: blur(5px);
    background-color: rgba(255, 255, 255, 0.95);
}
input:disabled, button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

</style>
